import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from 'lucide-react';
import { Fragment, type ReactNode, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export type PageSizeValue = 25 | 50 | 100 | 200 | 'all';

const PAGE_SIZE_OPTIONS: PageSizeValue[] = [25, 50, 100, 200, 'all'];

interface Props<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  /** Toolbar slot rendered top-right (e.g. "+ New row" button). */
  toolbar?: ReactNode;
  /** Initial page size (defaults to 50). */
  initialPageSize?: PageSizeValue;
  /** Optional global-filter placeholder copy. */
  filterPlaceholder?: string;
  /** Set of row keys (per `rowKey`) that should render an expansion sub-row. */
  expandedRows?: Set<string>;
  /** Stable identity for a row — keyed against `expandedRows` and `renderSubRow`. */
  rowKey?: (row: TData) => string;
  /** Renders the inner content of the expansion sub-row. */
  renderSubRow?: (row: TData) => ReactNode;
}

/**
 * Generic master-data table — TanStack v8 + shadcn `<Table>`.
 *
 * Wires up sortable headers, a substring filter (across every visible
 * column), pagination, and a page-size dropdown. Column-type detection /
 * inline editing live in the consumer's `columns` definition (see
 * `TableEditor.tsx`).
 */
export function DataTable<TData>({
  data,
  columns,
  toolbar,
  initialPageSize = 50,
  filterPlaceholder = 'Filter…',
  expandedRows,
  rowKey,
  renderSubRow,
}: Props<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pageSize, setPageSize] = useState<PageSizeValue>(initialPageSize);

  const effectivePageSize = pageSize === 'all' ? data.length || 1 : pageSize;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      pagination: { pageIndex: 0, pageSize: effectivePageSize },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _columnId, value: string) => {
      if (!value) return true;
      const needle = value.toLowerCase();
      const cells = row.getAllCells();
      for (const c of cells) {
        const v = c.getValue();
        if (v == null) continue;
        const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
        if (s.toLowerCase().includes(needle)) return true;
      }
      return false;
    },
    autoResetPageIndex: false,
    manualPagination: false,
  });

  // Keep page-size in sync with the underlying table state when "all" picked.
  useMemo(() => {
    table.setPageSize(effectivePageSize);
  }, [table, effectivePageSize]);

  const total = table.getFilteredRowModel().rows.length;
  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <Input
          placeholder={filterPlaceholder}
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="h-8 max-w-xs text-xs"
        />
        <div className="flex items-center gap-2">{toolbar}</div>
      </div>

      <div className="rounded-md border border-border">
        <Table>
          <TableHeader className="bg-muted/40">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => {
                  const canSort = h.column.getCanSort();
                  const sorted = h.column.getIsSorted();
                  return (
                    <TableHead key={h.id} className="text-xs">
                      {h.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={h.column.getToggleSortingHandler()}
                          className="flex items-center gap-1 text-foreground hover:text-foreground/80"
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {sorted === 'asc' ? (
                            <ArrowUpIcon className="size-3" />
                          ) : sorted === 'desc' ? (
                            <ArrowDownIcon className="size-3" />
                          ) : (
                            <ArrowUpDownIcon className="size-3 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(h.column.columnDef.header, h.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-6 text-center text-xs text-muted-foreground"
                >
                  No rows.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const showSubRow =
                  !!expandedRows &&
                  !!rowKey &&
                  !!renderSubRow &&
                  expandedRows.has(rowKey(row.original));
                return (
                  <Fragment key={row.id}>
                    <TableRow data-state={row.getIsSelected() ? 'selected' : undefined}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="text-xs align-top">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                    {showSubRow && (
                      <TableRow>
                        <TableCell
                          colSpan={row.getVisibleCells().length}
                          className="bg-muted/30 p-2"
                        >
                          {renderSubRow?.(row.original)}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {total} row{total === 1 ? '' : 's'}
          {pageSize !== 'all' && pageCount > 1 && ` · page ${pageIndex + 1}/${pageCount}`}
        </span>
        <div className="flex items-center gap-2">
          <span>Per page</span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) =>
              setPageSize(v === 'all' ? 'all' : (Number.parseInt(v, 10) as PageSizeValue))
            }
          >
            <SelectTrigger size="sm" className="h-7 w-20 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((o) => (
                <SelectItem key={String(o)} value={String(o)}>
                  {String(o)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Prev
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
